import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  aindaVaiAcontecer,
  erroDeStatus,
  jaPassou,
  STATUS_PARA_DATA_PASSADA,
} from '@/lib/reunioes';

interface Reuniao {
  id: string;
  condominio_id: string;
  titulo: string;
  data_reuniao: string;
  tipo: string;
  pauta?: string;
  ata_url?: string;
  ata_nome?: string;
  ata_tamanho?: string;
  participantes?: string;
  status: string;
  observacoes?: string;
  created_at: string;
}

const TIPOS_REUNIAO = [
  'Assembleia Geral Ordinária',
  'Assembleia Geral Extraordinária',
  'Reunião de Conselho',
  'Reunião de Condomínio',
];

const STATUS_LIST = ['Todas', 'Realizada', 'Agendada', 'Cancelada'];

function formatDate(iso: string): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatDateToInput(iso: string): string {
  if (!iso) return '';
  return iso.substring(0, 10);
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    Realizada: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Agendada: 'bg-amber-50 text-amber-700 border-amber-200',
    Cancelada: 'bg-red-50 text-red-700 border-red-200',
  };
  return map[status] || 'bg-background-100 text-foreground-600 border-background-300';
}

function tipoBadge(tipo: string) {
  if (tipo.includes('Ordinária')) return 'bg-primary-50 text-primary-700 border-primary-200';
  if (tipo.includes('Extraordinária')) return 'bg-accent-50 text-accent-700 border-accent-200';
  return 'bg-secondary-50 text-secondary-700 border-secondary-200';
}

const EMPTY_FORM = {
  titulo: '',
  data_reuniao: '',
  tipo: 'Reunião de Condomínio',
  pauta: '',
  participantes: '',
  status: 'Realizada',
  observacoes: '',
};

const BUCKET_ATAS = 'atas-reuniao';

export default function ReunioesTab() {
  const { id: condoId } = useParams<{ id: string }>();
  const { effectiveAccountId, user } = useAuth();

  const [reunioes, setReunioes] = useState<Reuniao[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState('Todas');
  const [busca, setBusca] = useState('');
  const [mostrarFormNovo, setMostrarFormNovo] = useState(false);
  const [novoForm, setNovoForm] = useState({ ...EMPTY_FORM });
  const [ataFile, setAtaFile] = useState<File | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Reuniao>>({});
  const [editAtaFile, setEditAtaFile] = useState<File | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Reuniao | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );
  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const fetchReunioes = useCallback(async () => {
    if (!condoId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .from('reunioes')
        .select('*')
        .eq('condominio_id', condoId)
        .order('data_reuniao', { ascending: false });

      if (fErr) throw new Error(fErr.message);
      setReunioes((data || []) as Reuniao[]);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar reuniões.');
      setReunioes([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    void fetchReunioes();
  }, [fetchReunioes]);

  // O bucket `atas-reuniao` e privado (migration 0013): guarda-se o caminho do
  // objeto, nao uma URL. A URL de leitura e assinada na hora do download e
  // expira — ata de reuniao e documento do condominio, nao arquivo publico.
  const uploadAta = async (
    file: File,
  ): Promise<{ caminho: string; nome: string; tamanho: string } | null> => {
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'pdf') {
        showToast('Apenas arquivos PDF são permitidos.', 'error');
        setError('Apenas arquivos PDF são permitidos.');
        return null;
      }
      if (file.size > 10 * 1024 * 1024) {
        showToast('O arquivo deve ter no máximo 10 MB.', 'error');
        setError('O arquivo deve ter no máximo 10 MB.');
        return null;
      }

      // O primeiro segmento do caminho e o condominio: e por ele que a policy
      // do bucket descobre a quem o arquivo pertence.
      const caminho = `${condoId}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      const { data, error: upErr } = await supabase.storage
        .from(BUCKET_ATAS)
        .upload(caminho, file, { contentType: 'application/pdf', upsert: true });

      if (upErr) throw upErr;

      return { caminho: data.path, nome: file.name, tamanho: formatFileSize(file.size) };
    } catch (e: any) {
      const mensagem = `Erro ao enviar o arquivo: ${e.message || 'tente novamente.'}`;
      showToast(mensagem, 'error');
      setError(mensagem);
      return null;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addReuniao = async () => {
    if (!condoId) return;
    if (!novoForm.titulo.trim()) {
      setError('O título da reunião é obrigatório.');
      return;
    }
    if (!novoForm.data_reuniao) {
      setError('A data da reunião é obrigatória.');
      return;
    }

    const erroStatus = erroDeStatus(novoForm.data_reuniao, novoForm.status);
    if (erroStatus) {
      setError(erroStatus);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let ataUrl: string | null = null;
      let ataNome: string | null = null;
      let ataTamanho: string | null = null;

      // O anexo e parte do cadastro, nao um extra: se o upload falha, o
      // cadastro para (P-38). Antes ele seguia sem o arquivo e a tela dizia
      // "Reuniao adicionada com sucesso!" — de onde vinha a impressao de que o
      // anexo sumia sozinho.
      if (ataFile) {
        const result = await uploadAta(ataFile);
        if (!result) {
          setSaving(false);
          return;
        }
        ataUrl = result.caminho;
        ataNome = result.nome;
        ataTamanho = result.tamanho;
      }

      const { error: iErr } = await supabase.from('reunioes').insert({
        condominio_id: condoId,
        account_id: effectiveAccountId,
        titulo: novoForm.titulo,
        data_reuniao: novoForm.data_reuniao,
        tipo: novoForm.tipo,
        pauta: novoForm.pauta || null,
        participantes: novoForm.participantes || null,
        status: novoForm.status,
        ata_url: ataUrl,
        ata_nome: ataNome,
        ata_tamanho: ataTamanho,
        observacoes: novoForm.observacoes || null,
      });

      if (iErr) throw new Error(iErr.message);

      setNovoForm({ ...EMPTY_FORM });
      setAtaFile(null);
      setMostrarFormNovo(false);
      showToast('Reunião adicionada com sucesso!', 'success');
      fetchReunioes();
    } catch (e: any) {
      setError(e.message || 'Erro ao adicionar reunião.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r: Reuniao) => {
    setEditingId(r.id);
    setEditData({ ...r });
    setEditAtaFile(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setEditAtaFile(null);
  };

  const saveEdit = async (id: string) => {
    if (!condoId) return;

    const erroStatus = erroDeStatus(editData.data_reuniao || '', editData.status || 'Realizada');
    if (erroStatus) {
      setError(erroStatus);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let ataUrl = editData.ata_url;
      let ataNome = editData.ata_nome;
      let ataTamanho = editData.ata_tamanho;

      // Mesma regra do cadastro: upload que falha nao vira "salvo com sucesso"
      // mantendo silenciosamente a ata anterior.
      if (editAtaFile) {
        const result = await uploadAta(editAtaFile);
        if (!result) {
          setSaving(false);
          return;
        }
        ataUrl = result.caminho;
        ataNome = result.nome;
        ataTamanho = result.tamanho;
      }

      const { error: uErr } = await supabase
        .from('reunioes')
        .update({
          titulo: editData.titulo,
          data_reuniao: editData.data_reuniao,
          tipo: editData.tipo,
          pauta: editData.pauta || null,
          participantes: editData.participantes || null,
          status: editData.status,
          ata_url: ataUrl || null,
          ata_nome: ataNome || null,
          ata_tamanho: ataTamanho || null,
          observacoes: editData.observacoes || null,
        })
        .eq('id', id);

      if (uErr) throw new Error(uErr.message);

      setEditingId(null);
      setEditData({});
      setEditAtaFile(null);
      showToast('Reunião atualizada com sucesso!', 'success');
      fetchReunioes();
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar alterações.');
    } finally {
      setSaving(false);
    }
  };

  const deleteReuniao = async () => {
    if (!deleteTarget) return;

    // Mesma regra do gatilho no banco, aqui so para explicar antes de tentar.
    if (!aindaVaiAcontecer(deleteTarget.data_reuniao)) {
      showToast('Reunião já ocorrida não pode ser excluída.', 'error');
      setDeleteTarget(null);
      return;
    }

    setSaving(true);
    try {
      const { error: dErr } = await supabase.from('reunioes').delete().eq('id', deleteTarget.id);
      if (dErr) throw new Error(dErr.message);
      showToast(`"${deleteTarget.titulo}" removida.`, 'success');
      setDeleteTarget(null);
      fetchReunioes();
    } catch (e: any) {
      showToast(e.message || 'Erro ao remover reunião.', 'error');
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const handleAtaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAtaFile(file);
  };

  const handleEditAtaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setEditAtaFile(file);
  };

  const downloadAta = async (referencia: string, nome: string) => {
    // Registros antigos guardaram a URL inteira; os novos guardam o caminho no
    // bucket privado, que precisa de assinatura para ser aberto.
    let url = referencia;

    if (!referencia.startsWith('http')) {
      const { data, error: sErr } = await supabase.storage
        .from(BUCKET_ATAS)
        .createSignedUrl(referencia, 60);

      if (sErr || !data) {
        showToast('Não foi possível abrir a ata.', 'error');
        return;
      }
      url = data.signedUrl;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filtradas = reunioes.filter((r) => {
    const matchStatus = filtroStatus === 'Todas' || r.status === filtroStatus;
    const matchBusca =
      r.titulo.toLowerCase().includes(busca.toLowerCase()) ||
      (r.pauta && r.pauta.toLowerCase().includes(busca.toLowerCase()));
    return matchStatus && matchBusca;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Carregando reuniões...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Error toast */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line text-red-500"></i>
          </div>
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-600"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Reuniões do Condomínio</h3>
          <p className="text-sm text-foreground-500 mt-0.5">
            {filtradas.length} de {reunioes.length} reunião(ões)
          </p>
        </div>
        <button
          onClick={() => {
            setMostrarFormNovo(!mostrarFormNovo);
            setError(null);
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </div>
          Nova Reunião
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar por título ou pauta..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          />
        </div>
        <div className="flex items-center gap-1.5 bg-background-100/60 rounded-full px-1 py-1 border border-background-200/60">
          {STATUS_LIST.map((s) => (
            <button
              key={s}
              onClick={() => setFiltroStatus(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                filtroStatus === s
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Novo formulário */}
      {mostrarFormNovo && (
        <div className="p-5 rounded-lg border border-primary-300/60 bg-primary-50/30">
          <h4 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-primary-100 flex items-center justify-center">
              <i className="ri-calendar-event-line text-primary-600 text-xs"></i>
            </div>
            Nova Reunião
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Título da reunião *
              </label>
              <input
                type="text"
                value={novoForm.titulo}
                onChange={(e) => setNovoForm({ ...novoForm, titulo: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                placeholder="Ex: Assembleia Geral Ordinária - Prestação de Contas"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Data da reunião *
              </label>
              <input
                type="date"
                value={novoForm.data_reuniao}
                onChange={(e) => {
                  // Escolher uma data passada com "Agendada" no campo seria
                  // preencher para depois ser recusado no salvar. O status
                  // acompanha a data; trocar para Cancelada continua possivel.
                  const data = e.target.value;
                  const status = erroDeStatus(data, novoForm.status)
                    ? STATUS_PARA_DATA_PASSADA
                    : novoForm.status;
                  setNovoForm({ ...novoForm, data_reuniao: data, status });
                }}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Tipo de reunião
              </label>
              <select
                value={novoForm.tipo}
                onChange={(e) => setNovoForm({ ...novoForm, tipo: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                {TIPOS_REUNIAO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Status</label>
              <select
                value={novoForm.status}
                onChange={(e) => setNovoForm({ ...novoForm, status: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="Realizada">Realizada</option>
                <option value="Agendada" disabled={jaPassou(novoForm.data_reuniao)}>
                  Agendada
                </option>
                <option value="Cancelada">Cancelada</option>
              </select>
              {jaPassou(novoForm.data_reuniao) && (
                <p className="text-xs text-foreground-500 mt-1">
                  Data no passado: a reunião só pode ser Realizada ou Cancelada.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Participantes
              </label>
              <input
                type="text"
                value={novoForm.participantes}
                onChange={(e) => setNovoForm({ ...novoForm, participantes: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                placeholder="Ex: Síndico, Conselho, Moradores"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Pauta</label>
              <textarea
                value={novoForm.pauta}
                onChange={(e) => setNovoForm({ ...novoForm, pauta: e.target.value })}
                rows={3}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-none"
                placeholder="Descreva os tópicos da pauta..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Ata da reunião (PDF)
                <span className="text-foreground-400 font-normal ml-1">opcional</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleAtaFileChange}
                className="w-full text-sm text-foreground-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary-100 file:text-secondary-700 hover:file:bg-secondary-200 file:cursor-pointer file:transition-colors"
              />
              {ataFile && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <i className="ri-file-pdf-line"></i> {ataFile.name} (
                  {(ataFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Observações
                <span className="text-foreground-400 font-normal ml-1">opcional</span>
              </label>
              <input
                type="text"
                value={novoForm.observacoes}
                onChange={(e) => setNovoForm({ ...novoForm, observacoes: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                placeholder="Ex: Aprovado por unanimidade..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-background-200/70">
            <button
              onClick={() => {
                setMostrarFormNovo(false);
                setNovoForm({ ...EMPTY_FORM });
                setAtaFile(null);
                setError(null);
              }}
              className="px-4 py-2 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={addReuniao}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Adicionar Reunião'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de reuniões */}
      {filtradas.length === 0 && !mostrarFormNovo ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-calendar-event-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">
            {busca || filtroStatus !== 'Todas'
              ? 'Nenhuma reunião encontrada com estes filtros.'
              : 'Nenhuma reunião cadastrada'}
          </p>
          <p className="text-sm text-foreground-500 mt-1">
            {busca || filtroStatus !== 'Todas'
              ? 'Tente ajustar os filtros.'
              : 'Clique em "Nova Reunião" para começar.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {filtradas.map((r) => (
            <div
              key={r.id}
              className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-background-300/80 transition-colors"
            >
              {editingId === r.id ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Título
                      </label>
                      <input
                        type="text"
                        value={editData.titulo || ''}
                        onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Data
                      </label>
                      <input
                        type="date"
                        value={formatDateToInput(editData.data_reuniao || '')}
                        onChange={(e) => {
                          const data = e.target.value;
                          const status = erroDeStatus(data, editData.status || 'Realizada')
                            ? STATUS_PARA_DATA_PASSADA
                            : editData.status;
                          setEditData({ ...editData, data_reuniao: data, status });
                        }}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Tipo
                      </label>
                      <select
                        value={editData.tipo || 'Reunião de Condomínio'}
                        onChange={(e) => setEditData({ ...editData, tipo: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      >
                        {TIPOS_REUNIAO.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Status
                      </label>
                      <select
                        value={editData.status || 'Realizada'}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      >
                        <option value="Realizada">Realizada</option>
                        <option
                          value="Agendada"
                          disabled={jaPassou(formatDateToInput(editData.data_reuniao || ''))}
                        >
                          Agendada
                        </option>
                        <option value="Cancelada">Cancelada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Participantes
                      </label>
                      <input
                        type="text"
                        value={editData.participantes || ''}
                        onChange={(e) =>
                          setEditData({ ...editData, participantes: e.target.value })
                        }
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Pauta
                      </label>
                      <textarea
                        value={editData.pauta || ''}
                        onChange={(e) => setEditData({ ...editData, pauta: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Ata (PDF)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          ref={editFileInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={handleEditAtaFileChange}
                          className="flex-1 text-xs text-foreground-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-secondary-100 file:text-secondary-700 hover:file:bg-secondary-200 file:cursor-pointer file:transition-colors"
                        />
                        {editData.ata_nome && !editAtaFile && (
                          <span className="text-xs text-foreground-500 whitespace-nowrap flex items-center gap-1">
                            <i className="ri-file-pdf-line text-red-500 text-sm"></i>
                            {editData.ata_nome}
                          </span>
                        )}
                      </div>
                      {editAtaFile && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <i className="ri-file-pdf-line"></i> Novo: {editAtaFile.name}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-foreground-600 mb-1">
                        Observações
                      </label>
                      <input
                        type="text"
                        value={editData.observacoes || ''}
                        onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-background-200/60">
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 rounded-md border border-background-300/60 text-xs text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => saveEdit(r.id)}
                      disabled={saving}
                      className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h4 className="text-sm font-semibold text-foreground-900">{r.titulo}</h4>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusBadge(r.status)}`}
                        >
                          {r.status}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${tipoBadge(r.tipo)}`}
                        >
                          {r.tipo}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-calendar-line text-xs"></i>
                          </div>
                          {formatDate(r.data_reuniao)}
                        </span>
                        {r.participantes && (
                          <span className="flex items-center gap-1">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-user-line text-xs"></i>
                            </div>
                            {r.participantes}
                          </span>
                        )}
                        {r.ata_nome && (
                          <span className="flex items-center gap-1">
                            <div className="w-3.5 h-3.5 flex items-center justify-center text-red-500">
                              <i className="ri-file-pdf-line text-xs"></i>
                            </div>
                            {r.ata_nome}
                            {r.ata_tamanho && (
                              <span className="text-foreground-400">({r.ata_tamanho})</span>
                            )}
                          </span>
                        )}
                      </div>

                      {r.pauta && (
                        <div className="mt-2 bg-background-100/60 rounded-md px-3 py-2">
                          <p className="text-xs font-medium text-foreground-600 mb-0.5">Pauta:</p>
                          <p className="text-xs text-foreground-500 leading-relaxed">{r.pauta}</p>
                        </div>
                      )}

                      {r.observacoes && (
                        <p className="text-xs text-foreground-500 mt-2 italic">{r.observacoes}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-0.5 shrink-0">
                      {r.ata_url && (
                        <button
                          onClick={() => void downloadAta(r.ata_url!, r.ata_nome || 'ata.pdf')}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Baixar ata"
                        >
                          <i className="ri-download-line text-sm"></i>
                        </button>
                      )}
                      <button
                        onClick={() => startEdit(r)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        disabled={!aindaVaiAcontecer(r.data_reuniao)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:hover:text-foreground-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title={
                          aindaVaiAcontecer(r.data_reuniao)
                            ? 'Remover'
                            : 'Reunião já ocorrida: faz parte do histórico do condomínio'
                        }
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-1">
              Tem certeza que deseja excluir permanentemente esta reunião? Só é possível excluir
              reuniões que ainda não ocorreram.
            </p>
            <p className="text-sm font-medium text-foreground-800 mb-6 bg-background-100/60 rounded px-3 py-2">
              {deleteTarget.titulo}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={deleteReuniao}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-background-50 text-foreground-800 border border-background-200/70'
            }`}
          >
            <i
              className={`${
                toast.type === 'success'
                  ? 'ri-checkbox-circle-line text-emerald-600'
                  : toast.type === 'error'
                    ? 'ri-close-circle-line text-red-600'
                    : 'ri-information-line text-primary-600'
              }`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
